import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';

import './index.css';
import Home from './routes/home/home2';
// import Home from './routes/home/home';
import ErrorPage from './error-page';
import { ThemeProvider } from './providers/ThemeProvider';
import { AppWithCookieConsent } from './App';
import { ApolloProvider } from './providers/ApolloProvider';
import { Login } from './routes/Login';
import { Signup } from './routes/Signup';
import { Pricing } from './routes/Pricing';
import { Support } from './routes/Support';
import { ForgotPassword } from './routes/ForgotPassword';
import { ResetPassword } from './routes/ResetPassword';
import { EventFanScreen } from './routes/fan/EventFanScreen';
import { AuthProvider } from './providers/AuthProvider';
import { EventStageScreen } from './routes/stage/EventStageScreen';
import { EventStageCodeScreen } from './routes/stage/EventStageCodeScreen';
import { IntlProvider } from './i18n';
import { Toaster } from './components/ui/sonner';
import { QuickSignup } from './routes/QuickSignup';
import { TooltipProvider } from './components/ui/tooltip';
import { FullScreenLoader } from './components/Loader';
import { adminRoutes, superAdminRoutes } from './routes/admin';
import BanUiPreviewPage from './routes/admin/screen/moderation/BanUiPreview';
import RaffleFanPreviewPage from './engagements/raffle/fan/RaffleFanPreviewPage';
import TriviaPreviewPage from './engagements/trivia/preview/TriviaPreviewPage';
import SlidesFreeformPreviewPage from './engagements/slides/preview/SlidesFreeformPreviewPage';
import { ProtectedArea } from './components/ProtectedArea';
import { Role } from './generated/graphql';
import { AuthSuccess } from './routes/auth/AuthSuccess';
import { Account } from './routes/account/Account';
import PrivacyPolicy from './routes/PrivacyPolicy';
import TermsOfUse from './routes/TermsOfUse';
import { AppleTouchIconUpdater } from './components/AppleTouchIconUpdater';

import {
  ACCOUNT_ROUTE,
  FAN_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  LOGIN_ROUTE,
  PRICING_ROUTE,
  SUPPORT_ROUTE,
  QUICK_SIGNUP_ROUTE,
  RESET_PASSWORD_ROUTE,
  SIGNUP_ROUTE,
  SLIDES_FREEFORM_PREVIEW_ROUTE,
  STAGE_ROUTE,
  STAGE_CODE_ROUTE,
} from './routes/consts';

const router = createBrowserRouter([
  {
    element: (
      <AppWithCookieConsent>
        <AppleTouchIconUpdater />
        <Outlet />
      </AppWithCookieConsent>
    ),
    children: [
      {
        path: '/',
        element: <Home />,
        errorElement: <ErrorPage />,
      },
      // privacy policy
      {
        path: '/privacy-policy',
        element: <PrivacyPolicy />,
        errorElement: <ErrorPage />,
      },
      // terms of service
      {
        path: '/terms-of-use',
        element: <TermsOfUse />,
        errorElement: <ErrorPage />,
      },
      {
        path: LOGIN_ROUTE,
        element: <Login />,
        errorElement: <ErrorPage />,
      },
      {
        path: SIGNUP_ROUTE,
        element: <Signup />,
        errorElement: <ErrorPage />,
      },
      {
        path: '/auth/success',
        element: <AuthSuccess />,
        errorElement: <ErrorPage />,
      },
      {
        path: FORGOT_PASSWORD_ROUTE,
        element: <ForgotPassword />,
        errorElement: <ErrorPage />,
      },
      {
        path: RESET_PASSWORD_ROUTE,
        element: <ResetPassword />,
        errorElement: <ErrorPage />,
      },
      {
        path: ACCOUNT_ROUTE,
        element: <Account />,
        errorElement: <ErrorPage />,
      },
      {
        path: STAGE_CODE_ROUTE,
        element: <EventStageCodeScreen />,
        errorElement: <ErrorPage />,
      },
      {
        path: STAGE_ROUTE,
        element: <EventStageScreen />,
        errorElement: <ErrorPage />,
      },
      {
        path: FAN_ROUTE,
        element: <EventFanScreen />,
        errorElement: <ErrorPage />,
      },
      {
        path: QUICK_SIGNUP_ROUTE,
        element: <QuickSignup />,
        errorElement: <ErrorPage />,
      },
      {
        path: PRICING_ROUTE,
        element: <Pricing />,
        errorElement: <ErrorPage />,
      },
      {
        path: SUPPORT_ROUTE,
        element: <Support />,
        errorElement: <ErrorPage />,
      },
      {
        path: '/ban-ui-preview',
        element: <BanUiPreviewPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: '/raffle-fan-preview',
        element: <RaffleFanPreviewPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: SLIDES_FREEFORM_PREVIEW_ROUTE,
        element: import.meta.env.DEV ? (
          <SlidesFreeformPreviewPage />
        ) : (
          <ProtectedArea roles={[Role.Admin]} redirectTo={LOGIN_ROUTE}>
            <SlidesFreeformPreviewPage />
          </ProtectedArea>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: '/trivia-preview',
        element: import.meta.env.DEV ? (
          <TriviaPreviewPage />
        ) : (
          <ProtectedArea roles={[Role.Admin]} redirectTo={LOGIN_ROUTE}>
            <TriviaPreviewPage />
          </ProtectedArea>
        ),
        errorElement: <ErrorPage />,
      },
      {
        // Admin pages
        ...adminRoutes,
        element: (
          <Suspense fallback={<FullScreenLoader />}>
            {adminRoutes.element}
          </Suspense>
        ),
      },
      {
        // Super admin pages
        ...superAdminRoutes,
        element: (
          <Suspense fallback={<FullScreenLoader />}>
            {superAdminRoutes.element}
          </Suspense>
        ),
      },
    ],
    errorElement: <ErrorPage />,
  },
]);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <IntlProvider>
      <ApolloProvider>
        <AuthProvider>
          <ThemeProvider defaultTheme="dark" storageKey="tcf-ui-theme">
            <TooltipProvider>
              <RouterProvider router={router} />
            </TooltipProvider>
            <Toaster richColors position="top-center" />
          </ThemeProvider>
        </AuthProvider>
      </ApolloProvider>
    </IntlProvider>
  </React.StrictMode>,
);
